import { useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { EntitlementsProject } from '@adsk/alloy-provider';
import {
  SORT_DIRECTION,
  SIDE_FILTERS_KEYS,
  DEFAULT_SORT_FIELD,
} from '../constants';

export type SideFiltersType = { [key: string]: string };

type FiltersType = {
  search?: string;
  offset: number;
  sort: {
    sortKey: keyof EntitlementsProject;
    sortValue: SORT_DIRECTION;
  };
  filters: SideFiltersType;
};

export const useFilters = () => {
  const [isSideFiltersOpen, setIsSideFiltersOpen] = useState(false);

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams(location.search);
  const {
    search,
    offset,
    sortKey: sortKeyFromUrl,
    sortValue: sortValueFromUrl,
    ...restUrlParams
  } = Object.fromEntries(searchParams);

  const sideFilters: SideFiltersType = {};
  const otherUrlParams: { [key: string]: string } = {}; // other url params who not relevant to side filters

  for (const [filterKey, filterValue] of Object.entries(restUrlParams)) {
    if (SIDE_FILTERS_KEYS.includes(filterKey)) {
      // filter only query params that relevant to side filters
      sideFilters[filterKey] = filterValue;
    } else {
      otherUrlParams[filterKey] = filterValue;
    }
  }

  const sortKey =
    (sortKeyFromUrl as keyof EntitlementsProject) || DEFAULT_SORT_FIELD;

  const sortValue =
    sortValueFromUrl === 'asc'
      ? SORT_DIRECTION.ASCENDING
      : SORT_DIRECTION.DESCENDING; // if not ascending or query param not exist

  const [state, setState] = useState<FiltersType>({
    search: search || '',
    offset: Number(offset) || 0,
    sort: {
      sortKey,
      sortValue,
    },
    filters: sideFilters,
  });

  function setStateAndQuery(nextState: FiltersType) {
    const { search, offset, sort, filters } = nextState;
    const newSearchParams = {
      ...(search && { search }),
      ...(offset && { offset: offset.toString() }),
      ...sort,
      ...filters,
      ...otherUrlParams,
    };

    const state = {
      ...(search && { search }),
      offset,
      filters,
      sort,
    };
    setSearchParams(newSearchParams, { replace: true });
    setState(state);
  }

  function resetSideFilters() {
    setStateAndQuery({
      search,
      sort: { sortKey, sortValue },
      filters: {},
      offset: 0,
    });
  }

  function filtersCount(f: SideFiltersType) {
    return Object.keys(f)?.length;
  }

  return {
    ...state,
    pageControls: {
      onPageChange: (index: number) =>
        setStateAndQuery({
          ...state,
          offset: index,
        }),
      onSortChange: ({
        columnId,
        desc,
      }: {
        columnId: keyof EntitlementsProject;
        desc: boolean;
      }) =>
        setStateAndQuery({
          ...state,
          sort: {
            sortKey: columnId,
            sortValue: desc
              ? SORT_DIRECTION.DESCENDING
              : SORT_DIRECTION.ASCENDING,
          },
        }),
      onSearchChange: (searchedValue: string) =>
        setStateAndQuery({
          ...state,
          search: searchedValue,
        }),
      onFilterChange: (name: string, value?: string) => {
        const newFilters = { ...state.filters };
        if (!value) {
          delete newFilters[name];
        } else {
          newFilters[name] = value;
        }
        setStateAndQuery({
          ...state,
          filters: newFilters,
        });
      },
    },
    sideFilters: {
      isOpen: isSideFiltersOpen,
      toggleIsOpen: () => setIsSideFiltersOpen(!isSideFiltersOpen),
      filtersCount: filtersCount(state.filters),
      clearFilters: resetSideFilters,
    },
  };
};
